import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { syncLscLiveSubscriptions } from '../api';
import { parseLscMutationError, type ParsedLscMutationError } from '../entitlementBillingErrors';
import type {
  LscLiveReadinessReport,
  LscLiveSubscriptionSyncItem,
  LscLiveSubscriptionSyncResult,
  LscLiveSubscriptionSyncSummary,
} from '../types';
import {
  countPlannedSubscriptionItems,
  countSuccessfulEligibleMembers,
} from './liveDetectionSyncUtils';
import LscMutationErrorAlert from './LscMutationErrorAlert';

export type LiveDetectionSyncModalProps = {
  open: boolean;
  leagueId: string;
  readiness: LscLiveReadinessReport | null;
  onClose: () => void;
  onSyncComplete: (result: LscLiveSubscriptionSyncResult) => void;
};

type ModalPhase = 'preview' | 'confirm' | 'syncing' | 'done' | 'error';

function failedItemsForDisplay(items: LscLiveSubscriptionSyncItem[]): LscLiveSubscriptionSyncItem[] {
  return items.filter((item) => item.action === 'failed');
}

export function LiveDetectionSyncModalView({
  open,
  phase,
  previewSummary,
  previewItems,
  syncResult,
  previewError,
  syncError,
  alreadyReadyCount,
  onClose,
  onConfirm,
  onRetryPreview,
}: {
  open: boolean;
  phase: ModalPhase;
  previewSummary: LscLiveSubscriptionSyncSummary | null;
  previewItems: LscLiveSubscriptionSyncItem[];
  syncResult: LscLiveSubscriptionSyncResult | null;
  previewError: ParsedLscMutationError | null;
  syncError: ParsedLscMutationError | null;
  alreadyReadyCount: number;
  onClose: () => void;
  onConfirm: () => void;
  onRetryPreview: () => void;
}) {
  if (!open) return null;

  const plannedCount = countPlannedSubscriptionItems(previewItems);
  const failedPreviewItems = failedItemsForDisplay(previewItems);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="live-detection-sync-title"
      data-testid="live-detection-sync-modal"
    >
      <div className="card max-w-lg w-full relative space-y-4">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-secondary-400 hover:text-white"
          aria-label="Close"
          disabled={phase === 'syncing'}
        >
          <X className="h-5 w-5" />
        </button>

        <div>
          <h2 id="live-detection-sync-title" className="text-lg font-semibold text-white">
            Repair Twitch alerts
          </h2>
          <p className="text-sm text-secondary-400 mt-1">
            Troubleshooting repair for missing or failed Twitch live/offline alerts. Normal setup is
            automatic for connected members.
          </p>
        </div>

        {phase === 'preview' ? (
          <p className="text-sm text-secondary-400" data-testid="live-detection-sync-loading">
            Loading preview...
          </p>
        ) : null}

        {previewError ? (
          <div className="space-y-3" data-testid="live-detection-sync-preview-error">
            <LscMutationErrorAlert error={previewError} />
            <button type="button" className="btn-secondary text-sm" onClick={onRetryPreview}>
              Retry preview
            </button>
          </div>
        ) : null}

        {previewSummary && (phase === 'confirm' || phase === 'syncing' || phase === 'done') ? (
          <dl
            className="grid gap-3 sm:grid-cols-2 text-sm rounded-lg border border-secondary-700/60 bg-secondary-900/40 px-4 py-3"
            data-testid="live-detection-sync-preview"
          >
            <div>
              <dt className="text-secondary-500">Eligible members</dt>
              <dd className="text-secondary-100">{previewSummary.eligible_members}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Already ready</dt>
              <dd className="text-secondary-100">{alreadyReadyCount}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Subscriptions planned</dt>
              <dd className="text-secondary-100">{plannedCount}</dd>
            </div>
            <div>
              <dt className="text-secondary-500">Skipped (missing Twitch)</dt>
              <dd className="text-secondary-100">{previewSummary.members_skipped}</dd>
            </div>
            {failedPreviewItems.length > 0 ? (
              <div className="sm:col-span-2">
                <dt className="text-secondary-500">Preview issues</dt>
                <dd className="text-amber-200">{failedPreviewItems.length} item(s)</dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {syncError ? (
          <div data-testid="live-detection-sync-error">
            <LscMutationErrorAlert error={syncError} />
          </div>
        ) : null}

        {syncResult && phase === 'done' ? (
          <div className="space-y-3" data-testid="live-detection-sync-result">
            {syncResult.summary.subscriptions_failed > 0 ? (
              <div className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0" aria-hidden />
                <div className="text-sm text-amber-100 space-y-1">
                  <p className="font-medium">Partial success</p>
                  <p>
                    Live detection synced for{' '}
                    {countSuccessfulEligibleMembers(syncResult.summary, syncResult.items)} of{' '}
                    {syncResult.summary.eligible_members} eligible members.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-emerald-200">
                Live detection synced for{' '}
                {countSuccessfulEligibleMembers(syncResult.summary, syncResult.items)} of{' '}
                {syncResult.summary.eligible_members} eligible members.
              </p>
            )}
            {failedItemsForDisplay(syncResult.items).length > 0 ? (
              <ul
                className="text-sm text-red-100/90 space-y-1 list-disc list-inside"
                data-testid="live-detection-sync-failed-list"
              >
                {failedItemsForDisplay(syncResult.items).map((item, index) => (
                  <li key={`${item.member_id}-${item.event_type}-${index}`}>
                    {item.twitch_login || item.member_id || 'Member'} · {item.event_type}:{' '}
                    {item.error || 'Sync failed'}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={phase === 'syncing'}
          >
            {phase === 'done' ? 'Close' : 'Cancel'}
          </button>
          {phase === 'confirm' ? (
            <button
              type="button"
              className="btn-primary"
              onClick={onConfirm}
              data-testid="live-detection-sync-confirm"
            >
              Repair Twitch alerts
            </button>
          ) : null}
          {phase === 'syncing' ? (
            <button type="button" className="btn-primary" disabled>
              Syncing...
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const LiveDetectionSyncModal: React.FC<LiveDetectionSyncModalProps> = ({
  open,
  leagueId,
  readiness,
  onClose,
  onSyncComplete,
}) => {
  const [phase, setPhase] = useState<ModalPhase>('preview');
  const [previewSummary, setPreviewSummary] = useState<LscLiveSubscriptionSyncSummary | null>(null);
  const [previewItems, setPreviewItems] = useState<LscLiveSubscriptionSyncItem[]>([]);
  const [syncResult, setSyncResult] = useState<LscLiveSubscriptionSyncResult | null>(null);
  const [previewError, setPreviewError] = useState<ParsedLscMutationError | null>(null);
  const [syncError, setSyncError] = useState<ParsedLscMutationError | null>(null);

  const alreadyReadyCount =
    readiness?.eventsub_subscription_status.members_with_enabled_pair ?? 0;

  const runPreview = useCallback(async () => {
    setPhase('preview');
    setPreviewError(null);
    setSyncError(null);
    setSyncResult(null);
    try {
      const result = await syncLscLiveSubscriptions(leagueId, { dry_run: true });
      setPreviewSummary(result.summary);
      setPreviewItems(result.items);
      setPhase('confirm');
    } catch (err) {
      setPreviewError(
        parseLscMutationError(
          err,
          'Could not preview live detection sync. Twitch may be temporarily unavailable — try again shortly.'
        )
      );
      setPhase('error');
    }
  }, [leagueId]);

  useEffect(() => {
    if (!open) {
      setPhase('preview');
      setPreviewSummary(null);
      setPreviewItems([]);
      setSyncResult(null);
      setPreviewError(null);
      setSyncError(null);
      return;
    }
    void runPreview();
  }, [open, runPreview]);

  const handleConfirm = async () => {
    if (phase === 'syncing') return;
    setPhase('syncing');
    setSyncError(null);
    try {
      const result = await syncLscLiveSubscriptions(leagueId, { dry_run: false });
      setSyncResult(result);
      setPhase('done');
      onSyncComplete(result);
    } catch (err) {
      setSyncError(
        parseLscMutationError(
          err,
          'Live detection sync failed. Twitch may be temporarily unavailable — try again shortly.'
        )
      );
      setPhase('confirm');
    }
  };

  return (
    <LiveDetectionSyncModalView
      open={open}
      phase={phase}
      previewSummary={previewSummary}
      previewItems={previewItems}
      syncResult={syncResult}
      previewError={previewError}
      syncError={syncError}
      alreadyReadyCount={alreadyReadyCount}
      onClose={onClose}
      onConfirm={() => {
        void handleConfirm();
      }}
      onRetryPreview={() => {
        void runPreview();
      }}
    />
  );
};

export default LiveDetectionSyncModal;
